import React, { FC, useMemo, RefObject, useEffect } from 'react'
import styled from 'styled-components'
import { prop } from 'ramda'
import {
  Button,
  Alert,
  ErrorNotification,
  Box,
  Link,
  useMediaSize,
  ButtonGroup,
  Space,
  TextBody,
} from '@ally/metronome-ui'
import {
  FromAccount,
  ToAccount,
  Amount,
  DeliveryDate,
  Transfer,
  useTransferFormsContext,
  TransferForm,
  TransferFormValues,
  transferToFormValues,
  useCustomerLimits,
} from '@ally/transfers-toolkit'
import { TransferAccount } from '@ally/data-types-transfer-accounts'
import { useInputStep } from '@ally/create-flow'
import { useHostServices } from '../../../HostServices'
import {
  getFromAccounts,
  getToAccounts,
  getTransferSelectionFlags,
} from './utils'

const InputWrapper = styled(Box)``

InputWrapper.defaultProps = { mt: 'xs' }

const initialValues: TransferFormValues = {
  fromAccount: '',
  toAccount: '',
  amount: null,
  deliveryDate: '',
}

function getInitialValues(current?: Transfer): TransferFormValues {
  return current ? transferToFormValues(current) : initialValues
}

export const InvestTransferMsg: FC<{
  showBankLink: boolean
}> = ({ showBankLink }) => {
  const { log } = useHostServices()

  useEffect(() => {
    ;(window as any).allytm.event('customEvent', {
      name: 'TRANSFER_WIDGET_INELIGIBLE',
      attr: { isInvest: true },
    })
    log.track('TRANSFER_WIDGET_INELIGIBLE_INVEST')
  }, [])

  return (
    <Box mt="sm">
      <TextBody tag="p" size="sm">
        You can<span>&#39;</span>t perform a one-time transfer with the selected
        account here.
      </TextBody>
      <TextBody tag="p" size="sm">
        Go to{' '}
        {showBankLink && (
          <>
            <Link text="Bank" to="/payments/transfers" /> or{' '}
          </>
        )}
        <Link text="Invest Transfers" to="/ext-invest-live/dashboard" /> to
        complete this transaction.
      </TextBody>
    </Box>
  )
}

const InputForm: FC<{ accounts: TransferAccount[] }> = ({ accounts }) => {
  const {
    formError,
    formErrorRef,
    values: { fromAccount, toAccount },
    meta: { shouldDisableSubmit, accountRestrictionError },
  } = useTransferFormsContext()

  const { log } = useHostServices()

  const { focusRef, saveError } = useInputStep<Transfer>()

  const fromAccounts = useMemo(() => getFromAccounts(accounts), [accounts])
  const eligibleFromAccounts = fromAccounts.filter(
    prop('isTransferEligibleFrom'),
  )
  const toAccounts = useMemo(() => getToAccounts(accounts), [accounts])
  const eligibleToAccounts = toAccounts.filter(prop('isTransferEligibleTo'))

  useEffect(() => {
    if (fromAccount) {
      ;(window as any).allytm.event('customEvent', {
        name: 'TRANSFER_WIDGET_START',
      })
      ;(window as any).allytm.event('clickEvent', {
        tagName: 'SELECT',
        allytmln: 'TW fromAccount',
      })
      log.track('TRANSFER_WIDGET_START')
    }
  }, [fromAccount])

  useEffect(() => {
    if (toAccount) {
      ;(window as any).allytm.event('clickEvent', {
        tagName: 'SELECT',
        allytmln: 'TW toAccount',
      })
      if (toAccount === fromAccount) {
        ;(window as any).allytm.event('customEvent', {
          name: 'TRANSFER_WIDGET_INELIGIBLE',
          attr: {
            eligibleFromAccounts: eligibleFromAccounts.length,
            eligibleToAccounts: eligibleToAccounts.length,
          },
        })
        log.track(
          `TRANSFER_WIDGET_INELIGIBLE_fromCount-${eligibleFromAccounts.length}_toCount-${eligibleToAccounts.length}`,
        )
      }
    }
  }, [toAccount])

  const {
    showRestOfForm,
    showInvestTransferMessage,
    showBankLink,
  } = getTransferSelectionFlags(accounts, fromAccount, toAccount)
  const restrictionOrSaveError = saveError || accountRestrictionError
  const isLgUp = useMediaSize('LgUp')

  useEffect(() => {
    if (restrictionOrSaveError) {
      ;(window as any).allytm.event('customEvent', {
        name: 'TRANSFER_WIDGET_FAIL',
        attr: {
          error: restrictionOrSaveError,
        },
      })
      log.track(`TRANSFER_WIDGET_FAIL-${restrictionOrSaveError}`)
    }
  }, [restrictionOrSaveError])

  const {
    isNewCustomer,
    limits,
    newCustomerLimitsEnabled,
  } = useCustomerLimits()

  const newCustomerLimit = limits?.newCustomerLimits.transferLimits.outbound

  const submit = showRestOfForm && (
    <Button
      type="submit"
      text="Review Transfer"
      variant={shouldDisableSubmit ? 'disabled' : 'primary'}
      disabled={shouldDisableSubmit}
      allytmln="TW Review Transfer"
    />
  )

  const moreTransfersOptions = (
    <Link
      text="More transfer options »"
      to="/payments/transfers"
      withAllyTM="TW More Transfer Options"
    />
  )

  return (
    <>
      {/* SERVICE ERROR */}
      {restrictionOrSaveError && (
        <Box mb="sm">
          <Alert
            disableContainer
            id={
              accountRestrictionError
                ? 'account-restriction-error'
                : 'save-error'
            }
            variant="error"
            heading={accountRestrictionError || saveError!.message}
            focusOnMount
          />
        </Box>
      )}

      {/* FORM-LEVEL ERROR */}
      <Box mb={formError ? 'xs' : 'none'}>
        <ErrorNotification
          ref={formErrorRef}
          tabIndex={-1}
          errorMessage={formError}
        />
      </Box>

      <InputWrapper mt="none">
        <FromAccount.Input
          accounts={fromAccounts}
          ref={focusRef as RefObject<any>}
          disableAutofill
        />
      </InputWrapper>

      <InputWrapper>
        <ToAccount.Input accounts={toAccounts} disableAutofill />
      </InputWrapper>

      {showRestOfForm && (
        <>
          <InputWrapper>
            <Amount.Input
              disableNewCustomerLimit={!newCustomerLimitsEnabled}
              isNewCustomer={isNewCustomer}
              newCustomerLimit={newCustomerLimit}
            />
          </InputWrapper>
          <InputWrapper>
            <DeliveryDate.Input />
          </InputWrapper>
        </>
      )}
      {showInvestTransferMessage ? (
        <InvestTransferMsg showBankLink={showBankLink} />
      ) : (
        <Space mt="md" p="sm">
          {isLgUp ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent={showRestOfForm ? 'space-between' : 'flex-end'}
            >
              {submit}
              {moreTransfersOptions}
            </Box>
          ) : (
            <ButtonGroup>
              {submit}
              {moreTransfersOptions}
            </ButtonGroup>
          )}
        </Space>
      )}
    </>
  )
}

export const Input: FC<{ accounts: TransferAccount[] }> = ({ accounts }) => {
  const { item: existingTransfer, review } = useInputStep<Transfer>()
  const hostServices = useHostServices()

  return (
    <TransferForm
      initialValues={getInitialValues(existingTransfer)}
      onSubmit={({ transfer }): void => {
        review(transfer)
      }}
      accounts={accounts}
      hostServices={hostServices}
    >
      <InputForm accounts={accounts} />
    </TransferForm>
  )
}
